import { apiRequest } from './api';

/** Sentinel region/category key for the "Другое / Boshqa" bucket (region-less lots). */
export const MARKET_OTHER = '__other__';

export interface MarketStats {
  generatedAt: string;
  activeTenders: number;
  activeBudgetUzs: number;
  newThisWeek: number;
  closingSoon: number;
  /** Competitive results only (API, 2026-10-17). */
  awarded12moCount: number;
  awarded12moVolumeUzs: number;
  /** Direct contracts over the same window — a separate figure, never part of
   *  the two above. Optional: a cached payload from before 2026-10-17 lacks it. */
  direct12moCount?: number;
  direct12moVolumeUzs?: number;
  byRegion: { region: string; name: string | null; tenders: number; budgetUzs: number }[];
  byCategory: { slug: string; name: string | null; tenders: number; budgetUzs: number }[];
  newPerWeek: { weekStart: string; count: number }[];
  /** No award-by-year series. It unioned the 2019–2023 registry with our own
   *  harvest and plotted our ingest coverage as if it were the market: 2024
   *  showed a two-thirds collapse and 2025 a twelve-fold jump, neither of which
   *  happened. Measured — 1,647 of 1,826 buyers (90%) peak in 2025. See
   *  `services/api/src/modules/catalog/market-stats.ts`. */
  competitionBySector: {
    section: string;
    name: string | null;
    medianParticipants: number;
    n: number;
  }[];
  discountBySector: {
    section: string;
    name: string | null;
    medianDiscountPct: number;
    n: number;
  }[];
}

/** An inclusive `YYYY-MM-DD` day window for the optional dashboard datepicker. */
export interface MarketRange {
  from: string;
  to: string;
}

/**
 * Public — aggregate market statistics (no individual names).
 *
 * With no `range` the URL is exactly `/catalog/market?lang=<lang>` — the
 * cached, prerendered all-time path — so SSR seeding and the crawled output are
 * untouched. A `range` appends `&from=&to=`, which the API recomputes live.
 */
export async function fetchMarketStats(lang: string, range?: MarketRange): Promise<MarketStats> {
  const window = range ? `&from=${range.from}&to=${range.to}` : '';
  return apiRequest<MarketStats>(`/catalog/market?lang=${lang}${window}`);
}
